import {css, cx} from "@panda/css";

import {
    BrandChartIcon,
    BrandCheckboxCheckedIcon,
    BrandCoinIcon,
    BrandEmailsStackIcon,
    BrandGuardIcon,
    BrandHandclapIcon,
    BrandLightBulbIcon,
    BrandMapMarkersIcon,
    BrandRankingIcon,
    BrandWeightIcon,
    CheckIcon
} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {replaceSpaceWithNonBreakingSpace} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {AuthModalBenefitDescriptionBulletItem} from "./AuthModalBenefitDescriptionBulletItem";
import {AuthModalVariant} from "./AuthModalHeaderVariants";

const registerBenefitsImg = require("../../images/register_benefits.png");

interface IProps {
    className?: string;
    variant: AuthModalVariant;
}

export const AuthModalBenefitDescription = ({className, variant}: IProps) => {
    if (variant === AuthModalVariant.HIDDEN_PRICES) {
        return <AuthModalBenefitDescriptionHiddenPrices className={className} />;
    }

    if (variant === AuthModalVariant.DOWNLOAD_REPORT) {
        return <AuthModalBenefitDescriptionDownloadRaport className={className} />;
    }

    return <AuthModalBenefitDescriptionDefault className={className} />;
};

const AuthModalBenefitDescriptionDownloadRaport = ({className}: {className?: string}) => {
    return (
        <div className={cx(wrapperStyle, className)}>
            <div className={coverImageWrapperStyle}>
                <Image width="226px" height="174px" alt="Korzyści z założenia konta" src={registerBenefitsImg} />
            </div>

            <Text variant="headline_2" align="center" fontFamily="secondary" mb={3}>
                Nie tylko raport… Zyskaj pełny obraz rynku i deweloperów
            </Text>

            <Text variant="body_copy_2" mb={4}>
                Raport o deweloperze to zestaw najważniejszych informacji, które pomogą Ci lepiej poznać firmę przed podjęciem decyzji o zakupie mieszkania.
            </Text>

            <Text variant="body_copy_0" mb={1.5}>
                W raporcie znajdziesz m.in.:
            </Text>

            <div className={bulletSectionStyle} style={{gap: "1.5rem"}}>
                <AuthModalBenefitDescriptionBulletItem
                    iconSlot={<BrandGuardIcon wrapperSize="5.6" size="5" wrapperColor="var(--colors-primary)" />}
                    descriptionSlot="Dane ogólne o firmie i jej doświadczeniu"
                    descriptionVariant="body_copy_0"
                />

                <AuthModalBenefitDescriptionBulletItem
                    iconSlot={<BrandMapMarkersIcon wrapperSize="5.6" size="5" wrapperColor="var(--colors-primary)" />}
                    descriptionSlot="Listę zrealizowanych inwestycji i aktualnych projektów w sprzedaży"
                    descriptionVariant="body_copy_0"
                />

                <AuthModalBenefitDescriptionBulletItem
                    iconSlot={<BrandRankingIcon wrapperSize="5.6" size="4.2" wrapperColor="var(--colors-primary)" className={emailsStackIconStyle} />}
                    descriptionSlot="Zdobyte nagrody i wyróżnienia"
                    descriptionVariant="body_copy_0"
                />

                <AuthModalBenefitDescriptionBulletItem
                    iconSlot={<BrandChartIcon wrapperSize="5.6" size="4.2" wrapperColor="var(--colors-primary)" className={emailsStackIconStyle} />}
                    descriptionSlot="Informacje o przynależności do branżowych organizacji i standardach jakości"
                    descriptionVariant="body_copy_0"
                />
            </div>
        </div>
    );
};

const AuthModalBenefitDescriptionDefault = ({className}: {className?: string}) => {
    return (
        <div className={cx(wrapperStyle, className)}>
            <div className={coverImageWrapperStyle}>
                <Image width="226px" height="174px" alt="Korzyści z założenia konta" src={registerBenefitsImg} />
            </div>
            <Text variant="headline_2" color="#000" align="center" fontFamily="secondary" className={titleStyle}>
                Cały proces poszukiwania mieszkania {replaceSpaceWithNonBreakingSpace("w jednym miejscu")}
            </Text>

            <div className={bulletSectionStyle}>
                <AuthModalBenefitDescriptionBulletItem
                    iconSlot={<BrandWeightIcon wrapperSize="5.6" size="5" wrapperColor="var(--colors-primary)" />}
                    titleSlot="Porównywanie ofert"
                    descriptionSlot="Dodaj mieszkania do ulubionych i porównaj je ze sobą. W ten sposób szybko zobaczysz różnice i łatwiej podejmiesz decyzję."
                />

                <AuthModalBenefitDescriptionBulletItem
                    iconSlot={<BrandCoinIcon wrapperSize="5.6" size="5" wrapperColor="var(--colors-primary)" />}
                    titleSlot="Śledzenie zmian cen"
                    descriptionSlot="Obserwuj, jak zmieniają się ceny interesujących Cię mieszkań i reaguj we właściwym momencie."
                />

                <AuthModalBenefitDescriptionBulletItem
                    iconSlot={<BrandEmailsStackIcon wrapperSize="5.6" size="4.2" wrapperColor="var(--colors-primary)" className={emailsStackIconStyle} />}
                    titleSlot="Informacje o promocjach"
                    descriptionSlot="Otrzymuj powiadomienia o nowych promocjach, aby żadna atrakcyjna oferta Ci nie umknęła."
                />
            </div>
        </div>
    );
};

const AuthModalBenefitDescriptionHiddenPrices = ({className}: {className?: string}) => {
    const {isMobile} = useUserDevice();
    const sectionIconSize = isMobile ? "4.2" : "5.2";
    const sectionIconWrapperSize = isMobile ? "4.4" : "5.6";

    return (
        <div className={cx(wrapperStyle, className)}>
            <div className={coverImageWrapperStyle}>
                <Image width="226px" height="174px" alt="Korzyści z założenia konta" src={registerBenefitsImg} />
            </div>
            <Text variant="headline_2" color="#000" align="center" fontFamily="secondary" className={titleStyle}>
                Cały proces poszukiwania mieszkania {replaceSpaceWithNonBreakingSpace("w jednym miejscu")}
            </Text>

            <div className={bulletSectionStyle}>
                {/* section 1: register benefits */}
                <div className={sectionWrapperStyle}>
                    <div className={sectionIconWrapperStyle}>
                        <BrandHandclapIcon wrapperSize={sectionIconWrapperSize} size={sectionIconSize} wrapperColor="var(--colors-primary)" />
                    </div>
                    <div className={sectionContentStyle}>
                        <Text variant="headline_4" className={sectionTitleStyle}>
                            Załóż bezpłatne konto i zyskaj
                        </Text>
                        <div className={benefitListStyle}>
                            <div className={benefitItemStyle}>
                                <div className={benefitItemIconStyle}>
                                    <CheckIcon size="2" wrapperSize="2" />
                                </div>
                                <Text variant="body_copy_1">
                                    <strong>dostęp do cen</strong> 85 000 mieszkań,
                                </Text>
                            </div>
                            <div className={benefitItemStyle}>
                                <div className={benefitItemIconStyle}>
                                    <CheckIcon size="2" wrapperSize="2" />
                                </div>
                                <Text variant="body_copy_1">
                                    <strong>promocje</strong> deweloperów dostępne tylko dla zalogowanych,
                                </Text>
                            </div>
                            <div className={benefitItemStyle}>
                                <div className={benefitItemIconStyle}>
                                    <CheckIcon size="2" wrapperSize="2" />
                                </div>
                                <Text variant="body_copy_1">
                                    wsparcie <strong>indywidualnego doradcy</strong> przy wyborze wymarzonego mieszkania.
                                </Text>
                            </div>
                        </div>
                    </div>
                </div>

                {/* section 2: additional benefits */}
                <div className={sectionWrapperStyle}>
                    <div className={sectionIconWrapperStyle}>
                        <BrandLightBulbIcon wrapperSize={sectionIconWrapperSize} size={sectionIconSize} wrapperColor="var(--colors-primary)" />
                    </div>
                    <div className={sectionContentStyle}>
                        <Text variant="headline_4" className={sectionTitleStyle}>
                            Dodatkowo otrzymasz:
                        </Text>
                        <div className={benefitListStyle}>
                            <div className={benefitItemStyle}>
                                <div className={benefitItemIconStyle}>
                                    <CheckIcon size="2" wrapperSize="2" />
                                </div>
                                <Text variant="body_copy_1">
                                    wygodną <strong>porównywarkę ofert,</strong>
                                </Text>
                            </div>
                            <div className={benefitItemStyle}>
                                <div className={benefitItemIconStyle}>
                                    <CheckIcon size="2" wrapperSize="2" />
                                </div>
                                <Text variant="body_copy_1">
                                    <strong>system rekomendacji</strong> dopasowany do Twoich potrzeb mieszkaniowych,
                                </Text>
                            </div>
                        </div>
                    </div>
                </div>

                {/* section 3: call to action */}
                <div className={sectionWrapperStyle}>
                    <div className={sectionIconWrapperStyle}>
                        <BrandCheckboxCheckedIcon wrapperSize={sectionIconWrapperSize} size={sectionIconSize} wrapperColor="var(--colors-primary)" />
                    </div>
                    <div className={sectionContentStyle}>
                        <Text variant="headline_4" className={sectionTitleStyle}>
                            Dołącz już dziś do grona <strong>275 000 zarejestrowanych użytkowników!</strong>
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

const wrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "100%"
});

const coverImageWrapperStyle = css({
    width: "100%",
    display: "flex",
    justifyContent: "center",
    marginBottom: 3
});

const titleStyle = css({
    marginBottom: 7
});

const bulletSectionStyle = css({
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
    rowGap: "4.4rem"
});

const emailsStackIconStyle = css({
    marginRight: "-1rem"
});

const sectionWrapperStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: 2
});

const sectionIconWrapperStyle = css({
    flexShrink: 0
});

const sectionContentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flex: 1
});

const sectionTitleStyle = css({
    margin: 0
});

const benefitListStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const benefitItemStyle = css({
    display: "flex",
    alignItems: "flex-start",
    gap: 1
});

const benefitItemIconStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: "0.2rem",
    width: "2rem",
    height: "2rem"
});
